import type { ThirdPartyTagRow } from '@/services/creatives/creativesApi'

/**
 * Finds the ad tags in text an ad server exported or a trafficker pasted —
 * a Campaign Manager tag export, rows copied out of Excel, or just markup
 * with a line of text above each tag. The server reads the sheet formats it
 * knows; this is the fallback for everything else.
 *
 * Tags are returned as written: the server recognises each ad server's tag
 * and fills in its cache buster and consent macros when the creative is saved.
 */
export function extractTags(text: string, { csv = false } = {}): ThirdPartyTagRow[] {
  const records = fromLabelledRecords(text)
  if (records.length) return records
  const rows = fromTable(text, csv)
  if (rows.length) return rows
  return fromFreeText(text)
}

/** Elements a tag can start with. Anything else that looks like markup is left as text. */
const TAG_ELEMENTS = new Set([
  'script',
  'noscript',
  'ins',
  'iframe',
  'div',
  'span',
  'a',
  'img',
  'object',
  'embed',
  'style',
  'link',
])
/** Elements whose content is not markup — closed by the first matching end tag. */
const RAW_TEXT = new Set(['script', 'noscript', 'iframe', 'style'])
const VOID = new Set(['img', 'embed', 'link'])

const SIZE = /\b(\d{2,4})\s*[x×]\s*(\d{2,4})\b/i

interface Span {
  start: number
  end: number
}

/** Where the opening tag starting at `start` ends — past its `>`, minding quoted attributes. */
function openingTagEnd(text: string, start: number): number {
  let quote = ''
  for (let i = start + 1; i < text.length; i++) {
    const c = text[i]
    if (quote) {
      if (c === quote) quote = ''
    } else if (c === '"' || c === "'") {
      quote = c
    } else if (c === '>') {
      return i + 1
    }
  }
  return -1
}

/** Where the element (or comment) opening at `start` ends, or -1 when it is never closed. */
function elementEnd(text: string, start: number, name: string): number {
  if (name === '!--') {
    const close = text.indexOf('-->', start + 4)
    return close < 0 ? -1 : close + 3
  }
  const openEnd = openingTagEnd(text, start)
  if (openEnd < 0) return -1
  if (VOID.has(name) || text[openEnd - 2] === '/') return openEnd

  if (RAW_TEXT.has(name)) {
    const close = new RegExp(`</${name}\\b[^>]*>`, 'gi')
    close.lastIndex = openEnd
    const m = close.exec(text)
    return m ? m.index + m[0].length : -1
  }

  // Nested elements of the same name count; script and the like are skipped
  // whole, so a "</div>" in a script's string does not close the div.
  const tags = new RegExp(`<(/?)${name}\\b[^>]*>|<(${[...RAW_TEXT].join('|')})\\b`, 'gi')
  tags.lastIndex = openEnd
  let depth = 1
  for (let m = tags.exec(text); m; m = tags.exec(text)) {
    if (m[2]) {
      const rawEnd = elementEnd(text, m.index, m[2].toLowerCase())
      if (rawEnd < 0) return -1
      tags.lastIndex = rawEnd
      continue
    }
    depth += m[1] ? -1 : 1
    if (depth === 0) return m.index + m[0].length
  }
  return -1
}

/**
 * The tags in a text: runs of elements with only whitespace between them, so
 * a `<script>` and its `<noscript>` stay one tag. A run of comments alone is not a tag.
 */
function findTagSpans(text: string): Span[] {
  const elements: (Span & { comment: boolean })[] = []
  const starts = /<(!--|[a-z][\w-]*)/gi
  for (let m = starts.exec(text); m; m = starts.exec(text)) {
    const name = m[1].toLowerCase()
    if (name !== '!--' && !TAG_ELEMENTS.has(name)) continue
    const end = elementEnd(text, m.index, name)
    if (end < 0) continue
    elements.push({ start: m.index, end, comment: name === '!--' })
    starts.lastIndex = end
  }

  const runs: (Span & { hasElement: boolean })[] = []
  for (const el of elements) {
    const last = runs[runs.length - 1]
    if (last && !text.slice(last.end, el.start).trim()) {
      last.end = el.end
      last.hasElement ||= !el.comment
    } else {
      runs.push({ start: el.start, end: el.end, hasElement: !el.comment })
    }
  }
  return runs.filter((r) => r.hasElement).map(({ start, end }) => ({ start, end }))
}

function firstTag(text: string): string | undefined {
  const span = findTagSpans(text)[0]
  return span && text.slice(span.start, span.end).trim()
}

function parseSize(text: string | undefined): { width: number; height: number } | undefined {
  const m = text && SIZE.exec(text)
  return m ? { width: Number(m[1]), height: Number(m[2]) } : undefined
}

/** The size a tag declares: DoubleClick's `sz=`, else its width and height attributes or styles. */
function tagSize(tag: string): { width?: number; height?: number } {
  const sz = /[;?&]sz=(\d+)x(\d+)/i.exec(tag)
  if (sz) return { width: Number(sz[1]), height: Number(sz[2]) }
  const width = /\bwidth\s*[:=]\s*["']?(\d+)/i.exec(tag)
  const height = /\bheight\s*[:=]\s*["']?(\d+)/i.exec(tag)
  return width && height ? { width: Number(width[1]), height: Number(height[1]) } : {}
}

function row(
  name: string,
  tag: string,
  size?: { width: number; height: number }
): ThirdPartyTagRow {
  return { name: name.trim(), tag, ...(size ?? tagSize(tag)) }
}

/**
 * Exports that label each record, like Campaign Manager's: a `Placement:` line
 * starts each one, then its size and the same tag in several formats. Only the
 * first format is taken — the export lists the recommended one first.
 */
const RECORD_START =
  /^[ \t]*(?:Placement|Placement name|Creative name|Tag name|Ad name)[ \t]*:[ \t]*(.+)$/gim

function fromLabelledRecords(text: string): ThirdPartyTagRow[] {
  const starts = [...text.matchAll(RECORD_START)]
  return starts.flatMap((m, i) => {
    const block = text.slice(m.index, starts[i + 1]?.index ?? text.length)
    const tag = firstTag(block)
    if (!tag) return []
    const size = parseSize(/^[ \t]*(?:Size|Dimensions)[ \t]*:(.*)$/im.exec(block)?.[1])
    return [row(m[1], tag, size)]
  })
}

/** Splits delimited text into rows of cells; quoted cells may hold delimiters, newlines and `""`. */
function parseDelimited(text: string, delimiter: string): string[][] {
  const rows: string[][] = []
  let cells: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        cell += '"'
        i++
      } else if (c === '"') {
        quoted = false
      } else {
        cell += c
      }
    } else if (c === '"' && !cell.trim()) {
      quoted = true
      cell = ''
    } else if (c === delimiter) {
      cells.push(cell)
      cell = ''
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++
      cells.push(cell)
      rows.push(cells)
      cells = []
      cell = ''
    } else {
      cell += c
    }
  }
  cells.push(cell)
  rows.push(cells)
  return rows.filter((r) => r.some((c) => c.trim()))
}

/**
 * Tabs when copied from a spreadsheet. A CSV file may use `,` or `;` instead —
 * whichever its first line has more of. Plain text is never split on those,
 * as a tag has commas of its own.
 */
function guessDelimiter(text: string, csv: boolean): string | undefined {
  if (text.includes('\t')) return '\t'
  if (!csv) return undefined
  const firstLine = text.split('\n', 1)[0]
  const commas = firstLine.split(',').length
  const semicolons = firstLine.split(';').length
  if (commas === 1 && semicolons === 1) return undefined
  return semicolons > commas ? ';' : ','
}

/**
 * Rows of a spreadsheet, pasted or saved as CSV: each row with a tag in one of
 * its cells is a creative. A header row, when there is one, says which column
 * holds the name and which the size; otherwise the longest plain cell is the name.
 */
function fromTable(text: string, csv: boolean): ThirdPartyTagRow[] {
  const delimiter = guessDelimiter(text, csv)
  if (!delimiter) return []
  const table = parseDelimited(text, delimiter)
  if (!table.some((cells) => cells.length > 1)) return []

  let nameCol = -1
  let sizeCol = -1
  const results: ThirdPartyTagRow[] = []
  for (const cells of table) {
    const tagCol = cells.findIndex((c) => firstTag(c))
    if (tagCol < 0) {
      if (!results.length) {
        nameCol = cells.findIndex((c) => /placement|creative|name|navn/i.test(c))
        sizeCol = cells.findIndex((c) => /size|dimension|størrelse/i.test(c))
      }
      continue
    }
    const tag = firstTag(cells[tagCol])!
    const plain = cells.filter((c, i) => i !== tagCol && !c.includes('<')).map((c) => c.trim())
    const name =
      (nameCol >= 0 && cells[nameCol]?.trim()) ||
      plain
        .filter((c) => !/^[\d\s.,:/x×-]*$/i.test(c) && !/^https?:\/\//i.test(c))
        .reduce((a, b) => (b.length > a.length ? b : a), '')
    const size =
      parseSize(sizeCol >= 0 ? cells[sizeCol] : undefined) ??
      parseSize(plain.find((c) => SIZE.test(c) && c.length <= 12)) ??
      parseSize(name)
    results.push(row(name, tag, size))
  }
  return results
}

/**
 * Tags in loose text: each tag is named by the nearest line of text above it,
 * passing over labels such as "JavaScript Tag:". The same tag twice is kept once.
 */
function fromFreeText(text: string): ThirdPartyTagRow[] {
  const seen = new Set<string>()
  let previousEnd = 0
  return findTagSpans(text).flatMap(({ start, end }) => {
    const tag = text.slice(start, end).trim()
    const before = text.slice(previousEnd, start)
    previousEnd = end
    if (seen.has(tag)) return []
    seen.add(tag)
    const name =
      before
        .split(/\r?\n/)
        .map((l) => l.trim())
        .reverse()
        .find((l) => l && !l.endsWith(':')) ?? ''
    return [row(name, tag, parseSize(name))]
  })
}
